import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, Package, Car, Monitor, Building2, Wrench, HelpCircle, DollarSign, Calendar, MapPin, FileText, AlertTriangle, Shield, Clock, CheckCircle } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import axios from 'axios';
import ExportButton from '../components/ExportButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#C41E3A', '#10B981', '#3B82F6', '#F59E0B', '#8B5CF6', '#EC4899'];

const CATEGORY_ICONS = {
  'Kenderaan': Car,
  'Peralatan': Wrench,
  'Komputer & IT': Monitor,
  'Perabot': Package,
  'Hartanah': Building2,
  'Lain-lain': HelpCircle
};

const ASSET_CATEGORIES = ['Kenderaan', 'Peralatan', 'Komputer & IT', 'Perabot', 'Hartanah', 'Lain-lain'];
const ASSET_STATUS = ['Aktif', 'Penyelenggaraan', 'Rosak', 'Dilupuskan'];
const MAINTENANCE_INTERVALS = [
  { value: 30, label: 'Bulanan (30 hari)' },
  { value: 90, label: 'Suku Tahunan (90 hari)' },
  { value: 180, label: 'Separuh Tahun (180 hari)' },
  { value: 365, label: 'Tahunan (365 hari)' },
  { value: 730, label: '2 Tahun (730 hari)' }
];

const formatRM = (value) => `RM${(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const getTodayDate = () => new Date().toISOString().split('T')[0];

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

const AlertCard = ({ icon: Icon, title, items, colorClass, emptyText }) => (
  <div className={`bg-white rounded-xl p-4 border-l-4 ${colorClass} shadow-sm`}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-5 h-5" />
      <h4 className="font-medium text-gray-800">{title}</h4>
      {items.length > 0 && (
        <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-medium ${colorClass.replace('border-l-', 'bg-').replace('-500', '-100')} ${colorClass.replace('border-l-', 'text-').replace('-500', '-700')}`}>
          {items.length}
        </span>
      )}
    </div>
    {items.length > 0 ? (
      <ul className="space-y-2">
        {items.slice(0, 5).map((item, idx) => (
          <li key={idx} className="flex items-center justify-between text-sm">
            <span className="text-gray-700">{item.name}</span>
            <span className={`text-xs ${item.priority === 'high' ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
              {item.days_remaining !== undefined ? `${item.days_remaining} hari lagi` : `${item.days_overdue} hari lepas`}
            </span>
          </li>
        ))}
        {items.length > 5 && <li className="text-xs text-gray-400">+{items.length - 5} lagi...</li>}
      </ul>
    ) : (
      <p className="text-sm text-gray-400">{emptyText}</p>
    )}
  </div>
);

const AssetForm = ({ onSubmit, onClose, initialData, categories }) => {
  const [formData, setFormData] = useState(initialData || {
    name: '',
    category: 'Peralatan',
    purchase_date: getTodayDate(),
    purchase_price: '',
    current_value: '',
    depreciation_rate: 10,
    location: '',
    serial_number: '',
    supplier: '',
    warranty_expiry: '',
    status: 'Aktif',
    notes: '',
    registration_number: '',
    address: '',
    maintenance_interval_days: 365,
    last_maintenance_date: '',
    next_maintenance_date: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      purchase_price: parseFloat(formData.purchase_price) || 0,
      current_value: parseFloat(formData.current_value) || 0,
      depreciation_rate: parseFloat(formData.depreciation_rate) || 10,
      maintenance_interval_days: parseInt(formData.maintenance_interval_days) || 365
    });
  };

  const isVehicle = formData.category === 'Kenderaan';
  const isProperty = formData.category === 'Hartanah';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Basic Info */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Package className="w-4 h-4" /> Maklumat Asas
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Nama Aset *</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" 
              required placeholder="Cth: Laptop Dell XPS 15" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Kategori *</label>
            <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm">
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm">
              {ASSET_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Financial Info */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <p className="text-sm font-semibold text-blue-700 mb-3 flex items-center gap-2">
          <DollarSign className="w-4 h-4" /> Maklumat Kewangan
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Tarikh Pembelian *</label>
            <input type="date" value={formData.purchase_date} onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" required />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Harga Belian (RM) *</label>
            <input type="number" step="0.01" value={formData.purchase_price} onChange={(e) => setFormData({...formData, purchase_price: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" required placeholder="0.00" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Nilai Semasa (RM)</label>
            <input type="number" step="0.01" value={formData.current_value} onChange={(e) => setFormData({...formData, current_value: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder="Auto-calculate if empty" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Kadar Susut Nilai (%/tahun)</label>
            <input type="number" step="0.1" value={formData.depreciation_rate} onChange={(e) => setFormData({...formData, depreciation_rate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder="10" />
          </div>
        </div>
      </div>

      {/* Location & Details */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <MapPin className="w-4 h-4" /> Lokasi & Butiran
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Lokasi</label>
            <input type="text" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder="Cth: Pejabat HQ" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">No. Siri</label>
            <input type="text" value={formData.serial_number} onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder="Serial number" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Pembekal</label>
            <input type="text" value={formData.supplier} onChange={(e) => setFormData({...formData, supplier: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder="Nama pembekal" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Tamat Waranti</label>
            <input type="date" value={formData.warranty_expiry} onChange={(e) => setFormData({...formData, warranty_expiry: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" />
          </div>
          
          {/* Vehicle specific */}
          {isVehicle && (
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">No. Pendaftaran Kenderaan</label>
              <input type="text" value={formData.registration_number} onChange={(e) => setFormData({...formData, registration_number: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder="Cth: WXY 1234" />
            </div>
          )}
          
          {/* Property specific */}
          {isProperty && (
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Alamat Hartanah</label>
              <textarea value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" rows={2} placeholder="Alamat penuh" />
            </div>
          )}
          
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Nota</label>
            <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" rows={2} placeholder="Nota tambahan..." />
          </div>
        </div>
      </div>

      {/* Maintenance Section */}
      <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
        <p className="text-sm font-semibold text-amber-700 mb-3 flex items-center gap-2">
          <Wrench className="w-4 h-4" /> Penyelenggaraan
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Kekerapan Penyelenggaraan</label>
            <select value={formData.maintenance_interval_days} onChange={(e) => setFormData({...formData, maintenance_interval_days: parseInt(e.target.value)})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm">
              {MAINTENANCE_INTERVALS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Tarikh Penyelenggaraan Terakhir</label>
            <input type="date" value={formData.last_maintenance_date || ''} onChange={(e) => setFormData({...formData, last_maintenance_date: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" />
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">Batal</button>
        <button type="submit" className="flex-1 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center justify-center gap-2 text-sm">
          <Save className="w-4 h-4" /> Simpan
        </button>
      </div>
    </form>
  );
};

const AssetPage = () => {
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showAlerts, setShowAlerts] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [assetsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/assets`),
        axios.get(`${API}/assets/summary/dashboard`)
      ]);
      setAssets(assetsRes.data || []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAdd = async (data) => {
    try {
      await axios.post(`${API}/assets`, data);
      await fetchData();
      setModalOpen(false);
    } catch (error) {
      console.error('Error adding asset:', error);
      alert('Gagal menambah aset');
    }
  };

  const handleEdit = async (data) => {
    try {
      await axios.put(`${API}/assets/${editingItem.id}`, data);
      await fetchData();
      setEditingItem(null);
      setModalOpen(false);
    } catch (error) {
      console.error('Error updating asset:', error);
      alert('Gagal mengemaskini aset');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Padam aset ini? Tindakan ini tidak boleh dibatalkan.')) {
      try {
        await axios.delete(`${API}/assets/${id}`);
        await fetchData();
      } catch (error) {
        console.error('Error deleting asset:', error);
        alert('Gagal memadam aset');
      }
    }
  };

  const handleRecordMaintenance = async (assetId) => {
    try {
      await axios.post(`${API}/assets/${assetId}/maintenance`);
      await fetchData();
      alert('Penyelenggaraan telah direkodkan!');
    } catch (error) {
      console.error('Error recording maintenance:', error);
      alert('Gagal merekod penyelenggaraan');
    }
  };

  // Filter assets
  const filteredAssets = assets.filter(asset => {
    if (filterCategory && asset.category !== filterCategory) return false;
    if (filterStatus && asset.status !== filterStatus) return false;
    return true;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Aktif': return 'bg-emerald-100 text-emerald-700';
      case 'Penyelenggaraan': return 'bg-amber-100 text-amber-700';
      case 'Rosak': return 'bg-red-100 text-red-700';
      case 'Dilupuskan': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
      </div>
    );
  }

  const categoryData = summary?.by_category || [];
  const statusData = summary?.by_status || [];
  const alerts = summary?.alerts || {};
  const totalAlerts = alerts.total_alerts || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Package className="w-7 h-7 text-red-700" /> Pengurusan Aset
          </h1>
          <p className="text-gray-500 text-sm">Senarai dan urus semua aset syarikat</p>
        </div>
        <div className="flex gap-2">
          <ExportButton type="asset" />
          <button onClick={() => { setEditingItem(null); setModalOpen(true); }}
            className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center gap-2" data-testid="add-asset-btn">
            <Plus className="w-4 h-4" /> Tambah Aset
          </button>
        </div>
      </div>

      {/* Alerts Banner */}
      {totalAlerts > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-red-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-gray-800">Peringatan Aset ({totalAlerts})</h3>
            </div>
            <button onClick={() => setShowAlerts(!showAlerts)} className="px-2.5 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded">
              {showAlerts ? 'Sembunyikan' : 'Tunjukkan'}
            </button>
          </div>
          {showAlerts && (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <AlertCard 
                icon={Shield} 
                title="Waranti Akan Tamat" 
                items={alerts.warranty_expiring_soon || []} 
                colorClass="border-l-amber-500"
                emptyText="Tiada waranti akan tamat"
              />
              <AlertCard 
                icon={Shield} 
                title="Waranti Telah Tamat" 
                items={alerts.warranty_expired || []} 
                colorClass="border-l-gray-500"
                emptyText="Tiada waranti tamat"
              />
              <AlertCard 
                icon={Wrench} 
                title="Penyelenggaraan Tertunggak" 
                items={alerts.maintenance_overdue || []} 
                colorClass="border-l-red-500"
                emptyText="Tiada penyelenggaraan tertunggak"
              />
              <AlertCard 
                icon={Clock} 
                title="Penyelenggaraan Akan Datang" 
                items={alerts.maintenance_due_soon || []} 
                colorClass="border-l-blue-500"
                emptyText="Tiada penyelenggaraan dijadualkan"
              />
            </div>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm border-l-4 border-l-blue-500">
          <p className="text-gray-500 text-sm mb-1">Jumlah Aset</p>
          <p className="text-2xl font-bold text-gray-800" data-testid="total-assets">{summary?.total_assets || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm border-l-4 border-l-emerald-500">
          <p className="text-gray-500 text-sm mb-1">Nilai Belian</p>
          <p className="text-2xl font-bold text-emerald-600">{formatRM(summary?.total_purchase_value)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm border-l-4 border-l-amber-500">
          <p className="text-gray-500 text-sm mb-1">Nilai Semasa</p>
          <p className="text-2xl font-bold text-amber-600">{formatRM(summary?.total_current_value)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm border-l-4 border-l-red-500">
          <p className="text-gray-500 text-sm mb-1">Susut Nilai Terkumpul</p>
          <p className="text-2xl font-bold text-red-600">{formatRM(summary?.total_depreciation)}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* By Category */}
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm">
          <h3 className="text-gray-800 font-medium mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-red-700" /> Aset Mengikut Kategori
          </h3>
          {categoryData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={categoryData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="current_value" nameKey="category">
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatRM(value)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-2">
                {categoryData.map((item, idx) => {
                  const Icon = CATEGORY_ICONS[item.category] || Package;
                  return (
                    <div key={item.category} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                        <Icon className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{item.category}</span>
                        <span className="text-gray-400">({item.count})</span>
                      </div>
                      <span className="font-medium text-gray-800">{formatRM(item.current_value)}</span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-gray-400">Tiada data aset</div>
          )}
        </div>

        {/* By Status */}
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm">
          <h3 className="text-gray-800 font-medium mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-red-700" /> Status Aset
          </h3>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={statusData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 10 }} />
                <YAxis dataKey="status" type="category" tick={{ fill: '#6b7280', fontSize: 10 }} width={100} />
                <Tooltip />
                <Bar dataKey="count" fill="#C41E3A" radius={[0, 4, 4, 0]} name="Bilangan" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-gray-400">Tiada data aset</div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
        <div className="flex flex-wrap gap-4 items-center">
          <span className="text-gray-600 text-sm font-medium">Filter:</span>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500">
            <option value="">Semua Kategori</option>
            {ASSET_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500">
            <option value="">Semua Status</option>
            {ASSET_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {(filterCategory || filterStatus) && (
            <button onClick={() => { setFilterCategory(''); setFilterStatus(''); }}
              className="text-red-600 text-sm hover:underline">Reset</button>
          )}
          <span className="text-gray-400 text-sm ml-auto">{filteredAssets.length} aset</span>
        </div>
      </div>

      {/* Assets Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-red-700 to-red-800">
          <h3 className="font-semibold text-white">Senarai Aset</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3 text-gray-600 font-medium">Nama Aset</th>
                <th className="text-left p-3 text-gray-600 font-medium">Kategori</th>
                <th className="text-left p-3 text-gray-600 font-medium">Tarikh Beli</th>
                <th className="text-right p-3 text-gray-600 font-medium">Harga Belian</th>
                <th className="text-right p-3 text-gray-600 font-medium">Nilai Semasa</th>
                <th className="text-center p-3 text-gray-600 font-medium">Umur</th>
                <th className="text-center p-3 text-gray-600 font-medium">Status</th>
                <th className="text-center p-3 text-gray-600 font-medium">Tindakan</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredAssets.map((asset) => {
                const Icon = CATEGORY_ICONS[asset.category] || Package;
                const depreciation = asset.purchase_price - asset.current_value;
                return (
                  <tr key={asset.id} className="hover:bg-gray-50">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-800">{asset.name}</p>
                          {asset.serial_number && <p className="text-xs text-gray-500">S/N: {asset.serial_number}</p>}
                          {asset.registration_number && <p className="text-xs text-gray-500">{asset.registration_number}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-gray-600">{asset.category}</td>
                    <td className="p-3 text-gray-600">{asset.purchase_date}</td>
                    <td className="p-3 text-right text-gray-700">{formatRM(asset.purchase_price)}</td>
                    <td className="p-3 text-right">
                      <p className="font-medium text-emerald-600">{formatRM(asset.current_value)}</p>
                      <p className="text-xs text-red-500">-{formatRM(depreciation)}</p>
                    </td>
                    <td className="p-3 text-center text-gray-600">{asset.age_years?.toFixed(1)} thn</td>
                    <td className="p-3 text-center">
                      <div className="space-y-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(asset.status)}`}>
                          {asset.status}
                        </span>
                        {asset.warranty_status === 'expiring_soon' && (
                          <p className="text-xs text-amber-600">Waranti: {asset.days_until_warranty_expiry}h</p>
                        )}
                        {asset.warranty_status === 'expired' && (
                          <p className="text-xs text-gray-400">Waranti tamat</p>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => handleRecordMaintenance(asset.id)}
                          title="Rekod Penyelenggaraan"
                          className="p-2 hover:bg-emerald-50 rounded text-gray-400 hover:text-emerald-600">
                          <CheckCircle className="w-4 h-4" />
                        </button>
                        <button onClick={() => { setEditingItem(asset); setModalOpen(true); }}
                          title="Edit"
                          className="p-2 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-700">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(asset.id)}
                          title="Padam"
                          className="p-2 hover:bg-red-50 rounded text-gray-400 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredAssets.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-gray-500">Tiada rekod aset</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={modalOpen} onClose={() => { setModalOpen(false); setEditingItem(null); }} title={editingItem ? 'Edit Aset' : 'Tambah Aset Baru'}>
        <AssetForm 
          onSubmit={editingItem ? handleEdit : handleAdd} 
          onClose={() => { setModalOpen(false); setEditingItem(null); }} 
          initialData={editingItem}
          categories={summary?.categories || ASSET_CATEGORIES}
        />
      </Modal>
    </div>
  );
};

export default AssetPage;
